<template>
    <div :class="menuStyleClasses" class="side-menu">
        <dx-list
            class="-color widgets-side-menu"
            :data-source="displayList"
            item-template="listItem"
            @item-click="onItemClick"
        >
            <dx-item-dragging :allow-reordering="true" :on-drag-end="onDragEnd" />
            <template #listItem="{ data }">
                <component :is="expanded ? WidgetsSideMenuItemExpanded : WidgetsSideMenuItemCollapsed" :data="data" />
            </template>
        </dx-list>

        <div class="flex w-full gap-2">
            <dx-button
                v-if="expanded"
                class="w-full collapse-button"
                icon="chevrondoubleright"
                text="Свернуть"
                @click="collapse"
            />
            <dx-button v-else icon="chevrondoubleleft" @click="expand" />
        </div>
    </div>
</template>

<script setup>
import Popup from 'devextreme/ui/popup';
import { DxButton } from 'devextreme-vue';
import { DxList, DxItemDragging } from 'devextreme-vue/list';
import { computed, onMounted, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { useWindowSize } from '@vueuse/core';

import WidgetsSideMenuItemCollapsed from '@/components/card-component/widgets-side-menu-item-collapsed.vue';
import WidgetsSideMenuItemExpanded from '@/components/card-component/widgets-side-menu-item-expanded.vue';
import { getFromLocalStorage, setInLocalStorage } from '@/utils/local-storage';
import { setIsWidgetOpening } from '@/layouts/main-layout/main-layout-data';

const props = defineProps({
    localStorageKey: {
        type: String,
        default: 'orderedWidgetList',
    },
});

const store = useStore();

store.dispatch('initVisibleIndexFromLocalStorage', { localStorageKey: props.localStorageKey });

const displayList = computed(() => store.getters.getSortedDisplayList());

const onDragEnd = event => {
    store.dispatch('reorderMetaWidgetPositions', {
        from: event.fromIndex,
        to: event.toIndex,
        localStorageKey: props.localStorageKey,
    });
};

const widthBreakpoint = 1440;

const pinnedStateKey = `${props.localStorageKey}PinnedState`;

const { width } = useWindowSize();

const expanded = ref(true);

const handleAutoModeChange = () => {
    const pinnedState = getFromLocalStorage(pinnedStateKey);

    if (!width.value || width.value <= widthBreakpoint) {
        expanded.value = false;
        return;
    }

    // режим сохранения настроек работает только когда ширина больше чем widthBreakpoint
    expanded.value = pinnedState === null || pinnedState === true;
};

onMounted(handleAutoModeChange);

watch(width, handleAutoModeChange);

const collapse = () => {
    expanded.value = false;
    setInLocalStorage(pinnedStateKey, false);
};

const expand = () => {
    expanded.value = true;
    setInLocalStorage(pinnedStateKey, true);
};

// const clearPinnedState = () => {
//   setInLocalStorage(pinnedStateKey, null);
// };

const menuStyleClasses = computed(() => ({
    'flex flex-col': true,
    'list-expanded': expanded.value,
    'list-collapsed': !expanded.value,
}));

const onItemClick = async event => {
    setIsWidgetOpening(true);
    const duplicatedWidget = await store.dispatch('addOpenedWidget', { value: event.itemData.name });
    if (duplicatedWidget) {
        const element = document.getElementById(duplicatedWidget.id);
        const instance = Popup.getInstance(element);
        instance.focus();
    }
};
</script>

<style lang="scss" scoped>
.list-collapsed {
    max-width: 250px;
}

.list-expanded {
    min-width: 400px;
    max-width: 400px;
    height: fit-content;
}

:deep(.dx-icon-chevrondoubleright) {
    color: white;
}

:deep(.collapse-button) {
    margin-top: 12px;
    font-size: 14px;
    height: 40px;
    color: #ffffff;
    background: #286eb6;
    border-radius: 8px;
}

.collapse-button:hover {
    background: #1c5ea2;
}

:deep(.dx-button-text) {
    text-transform: none;
}

.side-menu {
    padding: 20px;
    margin: 15px;
    background: #f6fafe;
    border-radius: 12px;
}

:deep(.item__title) {
    font-weight: 400 !important;
    font-size: 14px;
    text-align: left;
    white-space: nowrap;
    padding-left: 0;
    display: flex;
    align-items: center;
}

:deep(.item__icon) {
    min-width: 36px;
    min-height: 36px;
    max-width: 36px;
    max-height: 36px;
}

:deep(.dx-list:not(.dx-list-select-decorator-enabled) .dx-list-item) {
    background-color: #ffffff;
    align-items: center;
    border-radius: 5px;
    margin-bottom: 6px;
    display: flex;
    justify-content: center;
}

:deep(.dx-list:not(.dx-list-select-decorator-enabled) .dx-list-item:hover) {
    background-color: #f5f5f5;
}
</style>
